import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, History, Loader2, Megaphone, Users } from "lucide-react";
import { api, apiError } from "@/lib/api";

const STATUS_STYLE = {
  draft: "border-white/15 text-neutral-400",
  scheduled: "border-sky-500/40 text-sky-400",
  active: "border-emerald-500/40 text-emerald-400",
  paused: "border-amber-500/40 text-amber-400",
  ended: "border-white/10 text-neutral-600",
};

const RULE_LABELS = {
  new: "New customers", repeat: "Repeat customers", inactive: "Inactive customers",
  high_value: "High-value customers", outstanding_balance: "Outstanding balance",
  credit_customer: "PAQVERO Credit customers", abandoned_cart: "Abandoned carts",
  purchased_product: "Purchased product", purchased_category: "Purchased category",
  not_purchased_product: "Has NOT purchased product", not_purchased_category: "Has NOT purchased category",
};

function ruleText(r) {
  const bits = [];
  if (r.days) bits.push(`${r.days} days`);
  if (r.min_spend) bits.push(`≥ ₹${Number(r.min_spend).toLocaleString("en-IN")}`);
  if (r.product_id) bits.push(r.product_id);
  if (r.category) bits.push(r.category);
  return `${RULE_LABELS[r.type] || r.type}${bits.length ? ` — ${bits.join(", ")}` : ""}`;
}

export default function AdminCampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/campaigns/${id}`).then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e, "Couldn't load campaign")));
  }, [id]);
  useEffect(load, [load]);

  if (!data) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-neutral-600" /></div>;
  }
  const c = data.campaign;

  return (
    <div data-testid="admin-campaign-detail" className="max-w-4xl space-y-6">
      <button onClick={() => navigate("/admin/campaigns")} data-testid="campaign-detail-back"
        className="flex items-center gap-2 text-xs font-bold text-neutral-400 transition-colors hover:text-amber-400">
        <ArrowLeft className="h-3.5 w-3.5" /> All campaigns
      </button>

      <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 rounded-xl bg-amber-500/10 p-2.5 text-amber-400"><Megaphone className="h-5 w-5" /></span>
            <div>
              <h1 className="text-xl font-bold text-white" data-testid="campaign-detail-name">{c.name}</h1>
              {c.description && <p className="mt-0.5 text-xs text-neutral-500">{c.description}</p>}
            </div>
          </div>
          <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[c.status]}`}
            data-testid="campaign-detail-status">{c.status}</span>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-white/[0.06] bg-black/30 p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Window</p>
            <p className="mt-1 text-sm font-bold text-white" data-testid="campaign-detail-window">
              {new Date(c.start_date).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
              {" → "}
              {new Date(c.end_date).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
          <div className="rounded-xl border border-white/[0.06] bg-black/30 p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Live audience</p>
            <p className="mt-1 text-sm font-bold text-white" data-testid="campaign-detail-audience">{c.audience_size} customers</p>
          </div>
          <div className="rounded-xl border border-white/[0.06] bg-black/30 p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Engagement (real events only)</p>
            <p className="mt-1 text-sm font-bold text-white" data-testid="campaign-detail-stats">
              {c.stats.impressions} impressions · {c.stats.dismissals} dismissed
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">What customers see</p>
          <p className="mt-1.5 text-sm font-bold text-white" data-testid="campaign-detail-title">{c.title}</p>
          <p className="mt-0.5 text-xs text-neutral-400" data-testid="campaign-detail-message">{c.message}</p>
        </div>

        <div className="mt-5">
          <p className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
            <Users className="h-3.5 w-3.5" /> Audience criteria (combined with AND)
          </p>
          <div className="flex flex-wrap gap-2" data-testid="campaign-detail-rules">
            {(c.segment?.rules?.length ? c.segment.rules : []).map((r, i) => (
              <span key={i} className="rounded-full border border-white/10 px-3 py-1 text-[11px] font-semibold text-neutral-300">{ruleText(r)}</span>
            ))}
            {!c.segment?.rules?.length && (
              <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] font-semibold text-neutral-300">All customers</span>
            )}
          </div>
        </div>

        <div className="mt-5">
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">Audience preview (first 50)</p>
          <div className="max-h-48 space-y-1 overflow-y-auto rounded-xl border border-white/[0.06] bg-black/30 p-3" data-testid="campaign-detail-members" data-lenis-prevent>
            {data.audience_preview.length === 0 ? (
              <p className="text-xs text-neutral-600">No customers currently match.</p>
            ) : (
              data.audience_preview.map((m) => (
                <p key={m.id} className="text-xs text-neutral-300"><span className="font-bold text-white">{m.name}</span> <span className="text-neutral-500">{m.email}</span></p>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.07] bg-[#FFFEFA] p-6">
        <p className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
          <History className="h-3.5 w-3.5" /> Audit history
        </p>
        <div className="space-y-2" data-testid="campaign-detail-audit">
          {[...(c.audit || [])].reverse().map((a, i) => (
            <div key={i} className="flex items-start gap-3 text-xs" data-testid={`campaign-audit-${i}`}>
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
              <div>
                <p className="font-bold capitalize text-white">{a.action.replace(/_/g, " ")} <span className="font-medium text-neutral-500">by {a.actor}</span></p>
                <p className="text-[11px] text-neutral-500">
                  {new Date(a.at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  {a.details ? ` — ${a.details}` : ""}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
